// The Lobby's categories (owner's request, October 2026: "catégoriser les fonctionnalités, comme un
// architecte de jeux vidéo"): Jouer, Table, Profil, Social, Rang (the owner's order). A bar fixed at the
// bottom on phones and tablets (owner's choice: under the thumb), the same tabs in the header on a computer's
// large screen.
import React from "react";
import { Medal, Palette, Spade, UserRound, Users } from "lucide-react";
import { t } from "../../lib/i18n";

export const LOBBY_TABS = [
  { id: "play", icon: Spade },
  { id: "table", icon: Palette },
  { id: "profile", icon: UserRound },
  { id: "social", icon: Users },
  { id: "rank", icon: Medal },
];

// A red count (Social: invitations waiting) or a gold dot (Table: the table colours not seen yet).
function Badge({ value }) {
  if (!value) return null;
  if (value === true) {
    return (
      <span className="absolute -end-1.5 -top-0.5 flex h-2.5 w-2.5">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75 motion-reduce:hidden" />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-400" />
      </span>
    );
  }
  return (
    <span className="absolute -end-2.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] leading-none text-white ring-2 ring-slate-950">
      {value}
    </span>
  );
}

/** The bar at the bottom of the screen (phones, tablets). */
export function LobbyTabBar({ tab, onTab, badges = {} }) {
  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-30 border-t border-amber-500/15 bg-slate-950/90 backdrop-blur-xl safe-bottom lg:hidden"
      aria-label={t("nav.label")}
      data-testid="lobby-tabbar"
    >
      <div className="mx-auto grid max-w-lg grid-cols-5">
        {LOBBY_TABS.map(({ id, icon: Icon }) => {
          const on = tab === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onTab(id)}
              aria-current={on ? "page" : undefined}
              data-testid={`lobby-tab-${id}`}
              className={`relative flex min-w-0 flex-col items-center gap-0.5 pb-1 pt-1.5 transition-colors ${on ? "text-amber-300" : "text-slate-500 hover:text-slate-300"}`}
            >
              {on && <span className="absolute top-0 h-0.5 w-8 rounded-full bg-amber-400 shadow-[0_0_12px_rgba(251,191,36,0.8)]" />}
              <span className="relative">
                <Icon className={`h-[18px] w-[18px] ${on ? "drop-shadow-[0_0_8px_rgba(251,191,36,0.55)]" : ""}`} />
                <Badge value={badges[id]} />
              </span>
              <span className="max-w-full truncate px-0.5 text-[9px] uppercase tracking-[0.12em]">{t(`nav.${id}`)}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

/** The same tabs, inline in the header of a large screen. */
export function LobbyTabsInline({ tab, onTab, badges = {} }) {
  return (
    <nav className="hidden items-center gap-1 rounded-full border border-white/[0.06] bg-slate-950/50 p-1 lg:flex" aria-label={t("nav.label")}>
      {LOBBY_TABS.map(({ id, icon: Icon }) => {
        const on = tab === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onTab(id)}
            aria-current={on ? "page" : undefined}
            className={`relative flex items-center gap-2 rounded-full px-3.5 py-1 text-xs uppercase tracking-[0.14em] transition-colors ${
              on ? "bg-amber-400 text-slate-950" : "text-slate-400 hover:bg-white/5 hover:text-white"
            }`}
          >
            <span className="relative">
              <Icon className="h-4 w-4" />
              <Badge value={badges[id]} />
            </span>
            {t(`nav.${id}`)}
          </button>
        );
      })}
    </nav>
  );
}
